import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  RendererFactory2,
  SecurityContext,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenubarModule } from 'primeng/menubar';
import {
  combineLatest,
  debounceTime,
  filter,
  fromEvent,
  interval,
  Subscription,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthStore, LocalStorageJwtService } from '../../stores/auth';
import { CloudTagListStore } from '../../stores/cloud-tag/cloud-tag-list.store';
import { MyCollectionListStore } from '../../stores/my-collection/my-collection-list.store';
import { OrderDetailStore } from '../../stores/order/order-detail.store';
import { ProductsFrontEndStore } from '../../stores/products/products.store';
import { ProductFrontendService } from '../../stores/products/services/products.service';

import { AuthService } from '../../stores/auth/data-access/services/auth.service';
import { LanguagesListStore } from '../../stores/settings/languages-list.store';
import { LanguagesService } from '../../stores/settings/services/languages.service';

import { TopbarService } from '../../stores/services/topbar.service';
import { AppTopbarComponent } from '../topbar/app-topbar.component';

interface Language {
  name: string;
  code: string;
  icon: string;
}

@Component({
  selector: 'app-topbar-B2B',
  templateUrl: './app-topbar.component.html',
  styleUrls: ['./app-topbar.component.scss'],
  standalone: true,
  imports: [
    MenubarModule,
    AccordionModule,
    AvatarModule,
    DialogModule,
    AppTopbarComponent,
    BadgeModule,
    ButtonModule,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    InputIconModule,
    IconFieldModule,
    DropdownModule,
    FormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopbarB2BComponent implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef;
  router = inject(Router);
  private productFrontendService = inject(ProductFrontendService);
  private authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private topbarService = inject(TopbarService);
  private cloudTagListStore = inject(CloudTagListStore);
  private readonly localStorageJwtService = inject(LocalStorageJwtService);
  private domSanitizer = inject(DomSanitizer);
  private myCollectionListStore = inject(MyCollectionListStore);
  private $orderDetailStore = inject(OrderDetailStore);
  private productStore = inject(ProductsFrontEndStore);
  private languagesListStore = inject(LanguagesListStore);
  private languagesService = inject(LanguagesService);
  showScrollButton = false;
  popupTimeoutLogin = false;
  subscription!: Subscription;
  $currentUser = this.authStore.user;
  languages: Language[] = [
    { name: 'English', code: 'en', icon: 'pi pi-globe' },
    { name: 'French', code: 'fr', icon: 'pi pi-globe' },
  ];
  not_implement: any = 1;
  user_language: any = 'en';
  selectedLanguage: any =
    this.localStorageJwtService.getItemSTR('user_language');
  $languageList: any = this.languagesListStore.languageList.records;
  imgSrcUrl: string = `${environment.s3_url}/`;
  isOnSearch$: any;
  searchKey$: any;
  $isOnSearch: any = 0;
  $searchKey: any = '';

  is_use_campaign: any = 0;
  tracking_program: any = 0;

  sortedMenusItems = signal<any[]>([]);
  $menusItems = this.cloudTagListStore.findFullMenuTagForCompany.records;

  $totalCount = this.cloudTagListStore.findFullMenuTagForCompany.totalCount;
  $myCollectionListData = this.myCollectionListStore.custommycollection.records;
  $folderList = this.cloudTagListStore.folderList;
  $myCollectionTotalCount =
    this.myCollectionListStore.custommycollection.customTotalCount;
  $lastedOrder = this.$orderDetailStore.orderDetail;

  company_name = this.$currentUser()?.company?.company_name as any;
  userPermission: Record<string, any> = {};
  resourcePermission: Record<string, any> = {};

  searchControl: FormControl = new FormControl();

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showScrollButton = window.scrollY > 50; // Show when scrolled down 200px
  }

  // Scroll to top when clicked
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  safeHtml(content: any) {
    return this.domSanitizer.bypassSecurityTrustHtml(content);
  }

  sanitizeInput(input: string): string {
    const strippedInput = this.stripTags(input);
    return this.domSanitizer.sanitize(SecurityContext.URL, strippedInput) || '';
  }

  logOut() {
    this.cloudTagListStore.resetCallState();
    this.authStore.setLoggedOut(0);
    this.router.navigateByUrl('/auth/login');
  }

  constructor(
    rendererFactory: RendererFactory2,
    private cdr: ChangeDetectorRef,
  ) {
    effect(
      () => {
        this.init();
      },
      {
        allowSignalWrites: true,
      },
    );
  }
  displayTagName(item: any) {
    if (
      this.selectedLanguage != 'en' &&
      item['title_' + this.selectedLanguage]
    ) {
      return item['title_' + this.selectedLanguage];
    }
    return item['title'];
  }
  setLanguage() {
    this.languagesService.setLanguage(this.selectedLanguage);
    this.router.navigate([this.router.url]);
  }
  init() {
    if (this.$currentUser()?._id && !this.$totalCount()) {
      const params: any = [
        this.$currentUser()?.company_id,
        this.$currentUser()?.company?.use_company_data ?? 0,
        this.$currentUser(),
      ];
      this.cloudTagListStore.getMenuData(params);
      this.cloudTagListStore.getFolderMenuData(params);
    }
    this.is_use_campaign = this.$currentUser()?.company?.is_use_campaign ?? 0;
    this.tracking_program = this.$currentUser()?.company?.tracking_program ?? 0;
    this.user_language = this.$currentUser()?.user_language ?? 'en';
    this.userPermission = this.$currentUser()?.user_permission ?? {};
    this.resourcePermission = this.userPermission['resource'] ?? {};
  }
  ngOnInit() {
    this.init();

    const menusItems = this.$menusItems();
    if (menusItems) {
      this.sortedMenusItems.set(this.sortMenusItems(menusItems));
    }

    this.searchKey$ = combineLatest(this.productFrontendService.findKey$);

    if (
      this.$searchKey == '' &&
      this.localStorageJwtService.getItemSTR('currentSearch')
    ) {
      this.$searchKey = this.localStorageJwtService.getItemSTR('currentSearch');
      this.searchControl.setValue(this.$searchKey);
    }

    this.searchKey$.subscribe((response: any) => {
      if (response != '') {
        this.$searchKey = response;
      }
      if (this.$searchKey == '') {
        this.searchControl.reset();
      }
    });

    this.isOnSearch$ = combineLatest(this.productFrontendService.onSearch$);
    this.isOnSearch$.subscribe((response: any) => {
      this.$isOnSearch = response;
      if (!this.$isOnSearch[0]) {
        this.updateValue('');
      }
    });

    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        debounceTime(300),
        filter(() => this.searchControl.dirty),
      )
      .subscribe(() => {
        let searchQuery: any = this.sanitizeInput(this.searchControl.value);
        const checkSearchQuery = searchQuery ? searchQuery.trim() : '';
        if (checkSearchQuery && checkSearchQuery.length >= 2) {
          this.productStore.resetSearchBoardPageNumber(1);
          this.updateValue(searchQuery);
          this.productFrontendService.changeFindKey(checkSearchQuery);
          const link = `/productB2B/search/${encodeURIComponent(checkSearchQuery)}`;

          if (!this.$isOnSearch[0]) {
            this.router.navigateByUrl(link);
          }
        } else if (!checkSearchQuery) {
          this.updateValue('');
          this.productFrontendService.changeFindKey('');
          if (this.$isOnSearch[0]) {
            this.router.navigateByUrl('/');
          }
        }
      });

    this.subscription = interval(60000)
      .pipe(switchMap(() => this.authService.checkMinutesBeforeTimeout()))
      .subscribe((response: any) => {
        if (response) {
          this.popupTimeoutLogin = true;
        } else {
          this.popupTimeoutLogin = false;
        }
        this.cdr.detectChanges();
      });
  }
  reNewSession() {
    const id = this.$currentUser()?._id;
    const email = this.$currentUser()?.user_email;
    const data = {
      id: id,
      email: email,
      link: '-1',
      isB2bOrB2c: false,
    };
    this.authStore.apiLogin({
      data: data,
    });
    setTimeout(() => {
      this.popupTimeoutLogin = false;
      this.cdr.detectChanges();
    }, 1000);
  }
  updateValue(newValue: string) {
    this.searchControl.setValue(newValue);
  }
  stripTags(input: string): string {
    const div = document.createElement('div');
    div.innerHTML = input;
    return div.textContent || div.innerText || '';
  }
  switchNameOrder(fullName: string): string {
    const [lastName = '', firstName = ''] = fullName.split(' ');
    return `${firstName} ${lastName}`;
  }
  getInitials(fullName: string) {
    const nameParts = fullName.split(' ');
    return nameParts.map((part) => part[0]).join('');
  }
  displayImage(image_file: any) {
    if (!image_file || image_file == '') return '';
    if (image_file[0] == '/') {
      image_file = image_file.substring(1);
    }
    return this.imgSrcUrl + image_file;
  }
  sortMenusItems(items: any[]): any[] {
    return items.slice().sort((a, b) => a.title.localeCompare(b.title));
  }
  callSignIn() {
    this.topbarService.callSignIn();
  }
}
